import PopUpBase from '@components/PopUpBase';
import { IPopUp } from '@hooks/usePopUp';
import { Button, Slider, Stack, Typography } from '@mui/material';
import useMileSightSolar from '@pages/cameras/hook/useMilesightSolar';
import { useEffect } from 'react';
import { Control, Controller, FieldValues, Path, useForm } from 'react-hook-form';

type Props = Omit<IPopUp, 'onConfirm'> & {
    onConfirm?(data: FormValues): void;
};

type FormValues = {
    Brightness: number;
    Contrast: number;
    Saturation: number;
    Sharpness: number;
    Denoise: number;
};

export default function PopUpEditImageAdjustment(props: Props) {
    const { Solar4gImageInfo } = useMileSightSolar();

    const {
        control,
        setValue,
        reset,
        handleSubmit,
        clearErrors,
        watch,
        formState: { isDirty, isValid, errors },
    } = useForm<FormValues>({
        mode: 'all',
        defaultValues: {
            Brightness: 50,
            Contrast: 50,
            Saturation: 50,
            Sharpness: 50,
            Denoise: 50,
        },
    });

    const disabled = !(isValid && isDirty);

    const slidersForm: SliderControl<FormValues>[] = [
        {
            label: 'Brightness',
            name: 'Brightness',
        },
        {
            label: 'Constract',
            name: 'Contrast',
        },
        {
            label: 'Saturation',
            name: 'Saturation',
        },
        {
            label: 'Sharpness',
            name: 'Sharpness',
        },
        {
            label: 'Denoise',
            name: 'Denoise',
        },
    ];

    useEffect(() => {
        if (!props.open) return;

        reset({
            Brightness: Solar4gImageInfo?.Brightness,
            Contrast: Solar4gImageInfo?.Contrast,
            Saturation: Solar4gImageInfo?.Saturation,
            Sharpness: Solar4gImageInfo?.Sharpness,
            Denoise: Solar4gImageInfo?.Denoise,
        });
    }, [props.open]);

    return (
        <PopUpBase
            open={props.open}
            onClose={props.onClose}
            onConfirm={() => {}}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: '500px',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            title={<Typography variant="h4">Image adjustment</Typography>}
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            desc={
                <Stack width="100%" sx={{ background: '#FAFAFA', p: 2, mt: 2 }} spacing={3}>
                    {slidersForm.map((item) => (
                        <SliderControl key={item.name} control={control} label={item.label} name={item.name} />
                    ))}
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="cancel"
                        onClick={() => {
                            props.onClose?.();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={disabled}
                        onClick={handleSubmit((data) => {
                            props.onConfirm?.(data);
                        })}
                    >
                        Save
                    </Button>
                </Stack>
            }
        />
    );
}

export type SliderControl<T extends FieldValues> = {
    label: string;
    name: Path<T>;
};

export function SliderControl<T extends FieldValues>(props: SliderControl<T> & { control: Control<T, any> }) {
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography>{props.label}</Typography>
            <Controller
                name={props.name}
                control={props.control}
                render={({ field, fieldState: { error } }) => (
                    <Slider
                        {...field}
                        onChange={(e) => field.onChange(e)}
                        size="small"
                        sx={{ width: '70%' }}
                        valueLabelDisplay="auto"
                    />
                )}
            />
        </Stack>
    );
}
